<script setup lang="ts">
import { computed, ref } from 'vue'
import { useHead } from '@vueuse/head'
import { useI18n } from 'vue-i18n'
import { PhoneIcon } from '@heroicons/vue/24/outline'
import { useField, useForm } from 'vee-validate'

import { sendEmail } from '~/api/contact'
import { PHONE_NUMBER, PHONE_NUMBER_RAW } from '~/common/types'
import { AnimationLoader } from '~/components/ui/animations'
import TurnstileWidget from '~/components/ui/turnstile-widget.vue'
import { type ContactSchemaType, contactSchema } from '~/schemas'

const { locale, t } = useI18n()

const { isSubmitting, meta, handleSubmit, resetForm } = useForm<ContactSchemaType>({
  validationSchema: contactSchema,
})

const { value: name, meta: nameMeta, errorMessage: nameError } = useField<string>('name')
const { value: from, meta: fromMeta, errorMessage: fromError } = useField<string>('from')
const { value: subject, meta: subjectMeta, errorMessage: subjectError } = useField<string>('subject')
const { value: text, meta: textMeta, errorMessage: textError } = useField<string>('text')

useHead({
  title: computed(() => `${t('modules.nav.contact')} - ${t('project.name')}`),
})

const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string
const turnstileToken = ref<string | null>(null)
const turnstileWidget = ref<InstanceType<typeof TurnstileWidget>>()
const hasBeenSent = ref(false)

const onSubmit = handleSubmit(async (values) => {
  if (!turnstileToken.value)
    return

  try {
    await sendEmail(values, turnstileToken.value)
    hasBeenSent.value = true
    resetForm()
    turnstileToken.value = null
  }
  catch {
    // Errors are handled in the next `finally` block.
  }
  finally {
    turnstileWidget.value?.reset()
    setTimeout(() => {
      if (hasBeenSent.value)
        window.alert(t('pages.contact.response.success'))
      else
        window.alert(t('pages.contact.response.error'))

      hasBeenSent.value = false
    }, 100)
  }
})
</script>

<template>
  <div class="max-w-7xl mx-auto px-4 md:px-8 py-16">
    <div class="flex flex-wrap -m-4">
      <div class="w-full p-4 md:w-5/12 lg:w-1/2">
        <div class="bg-white rounded-lg p-8 shadow-sm">
          <h2 class="text-2xl font-extrabold tracking-tight text-gray-900 sm:text-3xl">
            {{ $t('pages.contact.title') }}
          </h2>
          <p class="mt-3 text-lg leading-6 text-gray-500">
            {{ t('pages.contact.helper.text') }}
            <a :href="`https://wa.me/${PHONE_NUMBER}?lang=${locale}`"
               target="_blank"
               class="hover:underline"
            >WhatsApp</a>.
          </p>
          <dl class="mt-8 text-base text-gray-500">
            <div>
              <dt class="sr-only">
                {{ $t('pages.contact.helper.postal-address') }}
              </dt>
              <dd>
                <p>{{ $t('project.address') }}</p>
              </dd>
            </div>
            <div class="mt-6">
              <dt class="sr-only">
                {{ $t('pages.contact.helper.phone-number') }}
              </dt>
              <dd class="flex">
                <PhoneIcon class="flex-shrink-0 h-6 w-6 text-gray-400" aria-hidden="true" />
                <a class="ml-3 hover:underline" :href="`tel:${PHONE_NUMBER_RAW}`">
                  {{ PHONE_NUMBER }}
                </a>
              </dd>
            </div>
          </dl>
        </div>
      </div>
      <div class="w-full p-4 md:w-7/12 lg:w-1/2">
        <div class="bg-white rounded-lg p-8 shadow-sm">
          <form class="space-y-6"
                @submit="onSubmit"
          >
            <div>
              <label for="name" class="sr-only">
                {{ $t('pages.contact.form.full-name') }}
              </label>
              <input id="name"
                     v-model="name"
                     required
                     :name="$t('pages.contact.form.full-name')"
                     :placeholder="`${$t('pages.contact.form.full-name')}*`"
                     type="text"
                     class="bg-gray-50 block w-full shadow-sm py-3 px-4 placeholder-gray-500 focus:ring-primary-500 focus:border-primary-500 border-gray-300 rounded-md"
              >
              <span v-if="nameMeta.touched && nameError"
                    class="text-red-500 text-xs"
              >
                {{ nameError }}
              </span>
            </div>
            <div>
              <label for="from" class="sr-only">
                {{ $t('pages.contact.form.email') }}
              </label>
              <input id="from"
                     v-model="from"
                     required
                     :name="$t('pages.contact.form.email')"
                     :placeholder="`${$t('pages.contact.form.email')}*`"
                     type="email"
                     class="bg-gray-50 block w-full shadow-sm py-3 px-4 placeholder-gray-500 focus:ring-primary-500 focus:border-primary-500 border-gray-300 rounded-md"
              >
              <span v-if="fromMeta.touched && nameError"
                    class="text-red-500 text-xs"
              >
                {{ fromError }}
              </span>
            </div>
            <div>
              <label for="subject" class="sr-only">
                {{ $t('pages.contact.form.subject') }}
              </label>
              <input id="subject"
                     v-model="subject"
                     required
                     :name="$t('pages.contact.form.subject')"
                     :placeholder="`${$t('pages.contact.form.subject')}*`"
                     type="text"
                     class="bg-gray-50 block w-full shadow-sm py-3 px-4 placeholder-gray-500 focus:ring-primary-500 focus:border-primary-500 border-gray-300 rounded-md"
              >
              <span v-if="subjectMeta.touched && nameError"
                    class="text-red-500 text-xs"
              >
                {{ subjectError }}
              </span>
            </div>
            <div>
              <label for="text" class="sr-only">
                {{ $t('pages.contact.form.message') }}
              </label>
              <textarea id="text"
                        v-model="text"
                        required
                        :name="$t('pages.contact.form.message')"
                        :placeholder="`${$t('pages.contact.form.message')}*`"
                        rows="4"
                        class="bg-gray-50 block w-full shadow-sm py-3 px-4 placeholder-gray-500 focus:ring-primary-500 focus:border-primary-500 border border-gray-300 rounded-md"
              />
              <span v-if="textMeta.touched && nameError"
                    class="text-red-500 text-xs"
              >
                {{ textError }}
              </span>
            </div>
            <TurnstileWidget
              ref="turnstileWidget"
              :site-key="turnstileSiteKey"
              @verify="token => turnstileToken = token"
              @expire="turnstileToken = null"
              @error="turnstileToken = null"
            />
            <button type="submit"
                    :disabled="isSubmitting || !meta.valid || !turnstileToken"
                    class="relative bg-gray-800 border border-transparent rounded-md shadow-sm py-2 px-4 inline-flex justify-center text-sm font-medium text-white hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900"
            >
              <span :class="{ 'opacity-0': isSubmitting }">
                {{ $t('pages.contact.form.submit') }}
              </span>

              <span v-show="isSubmitting"
                    class="w-5 h-5 is-centered absolute"
              >
                <AnimationLoader fill="#FFF"
                                 class="w-full h-auto"
                />
              </span>
            </button>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>
